import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Header, Segment } from 'semantic-ui-react';
import { Controller, useFormContext } from 'react-hook-form';
import { DateInput, DateTimeUtils } from 'dyl-components';

import productsActions from "actions/products";

import feeSchema from 'shared/schemas/fees/feeSchema';
import { StringUtils } from 'utils';

const FeeInfo = ({ setIsNameDuplicate }) => {
    const dispatch = useDispatch();
    const { control, clearErrors, setError, type, feeBeingEdited } = useFormContext();

    const [typingTimeout, setTypingtimeout] = useState(null);

    const capitalizedType = StringUtils.capitalize(type);
    const { fee } = useSelector((state) => state.product_fees);
    const isCheckingForDuplicates = useSelector(state => state.products.isCheckingForDuplicates);

    const generateEffectiveDateMessage = (status, date) => {
        let message = null;
        if (!DateTimeUtils.isBeforeOrOnCurrentDate(date, DateTimeUtils.DATE_FORMAT)) {
            if (!feeBeingEdited) {
                if (status === 'active') {
                    message = `This ${type} is inactive until ${date}`;
                }
            } else {
                let { status: originalStatus } = fee;
                if (originalStatus === 'active' && status === 'discontinued') {
                    message = `The ${type} is active until ${date}`;
                }
                if (originalStatus === 'inactive' && status === 'discontinued') {
                    message = `The ${type} is inactive until ${date}`;
                }
            }
        }

        return (
            <p>
                <em>{message}</em>
            </p>
        );
    }

    return (
        <Segment>
            <Header as="h3" color="primary">{StringUtils.capitalize(type)} Info</Header>
            <Controller
                control={control}
                name="name"
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Input
                        name={name}
                        value={value}
                        onChange={async (_, { name, value }) => {
                            if (typingTimeout) {
                                clearTimeout(typingTimeout)
                            }
                            onChange({ target: { name, value } });
                            const isValid = await feeSchema.name.isValid(value);
                            if (isValid && value !== '') {
                                setTypingtimeout(
                                    setTimeout(async () => {
                                        await clearErrors(name);
                                        await setIsNameDuplicate(false);
                                        const isDuplicate = (await dispatch(productsActions.isDuplicate({ name: value, type, ...(feeBeingEdited ? { id: feeBeingEdited } : {}) })));
                                        if (!isDuplicate) {
                                            await setError(name, {
                                                type: `unique_${type}`,
                                                message: `${capitalizedType} name already exists!`
                                            })
                                            await setIsNameDuplicate(true);
                                        }
                                    })
                                );
                            }
                        }}
                        label={`${capitalizedType} Name`}
                        required
                        error={error?.message}
                        loading={isCheckingForDuplicates}
                    />
                )}
            />
            <Controller
                control={control}
                name="description"
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.TextArea
                        name={name}
                        value={value}
                        onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                        error={error?.message}
                        label={`${capitalizedType} Description`}
                    />
                )}
            />
            <Form.Group widths={"equal"}>
                <Controller
                    control={control}
                    name="status"
                    render={({ field: { name, value: status, onChange }, fieldState: { error } }) => (
                        <>
                            <Form.Select
                                name={name}
                                value={status}
                                onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                                label="Status"
                                required
                                options={[
                                    {
                                        key: "active", value: "active", text: "Active"
                                    },
                                    {
                                        key: "inactive", value: "inactive", text: "Inactive"
                                    },
                                    ...(feeBeingEdited ? [{
                                        key: "discontinued", value: "discontinued", text: "Discontinued"
                                    }] : [])
                                ]}
                                error={error?.message}
                                selectOnBlur={false}
                            />
                            <Controller
                                control={control}
                                name="active_date"
                                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                    <Form.Field as='div'>
                                        <Form.Field
                                            control={DateInput}
                                            name={name}
                                            value={value}
                                            onChange={(_, { name, value }) => { onChange({ target: { name, value } }) }}
                                            label="Effective Date"
                                            error={error?.message}
                                            dateFormat={DateTimeUtils.DATE_FORMAT}
                                        />
                                        {DateTimeUtils.isValid(value, DateTimeUtils.DATE_FORMAT) && (
                                            generateEffectiveDateMessage(status, value)
                                        )}
                                    </Form.Field>
                                )}
                            />
                        </>
                    )}
                />
            </Form.Group>
        </Segment>
    );
}

export default FeeInfo;
